// Rótulos PT-BR dos estados da denúncia.
//
// Existe porque as 4 SPAs imprimiam `state.kind` CRU na tela — o identificador da
// união discriminada da máquina de estados, em CamelCase e sem acento. O
// denunciante voltava ao canal e lia "AguardandoRetornoComite".
//
// Duas tabelas, não uma: o denunciante e o comitê veem o MESMO estado com
// necessidades diferentes de informação. Ver `ESTADO_LABEL_DENUNCIANTE` abaixo
// para o caso em que os dois divergem de propósito.
//
// Fica em `core` (e não no i18n de cada SPA) porque a fonte do rótulo é a própria
// união de estados: quando um `kind` novo entra em `state-machine.ts`, o
// `Record<DenunciaState['kind'], string>` abaixo fica VERMELHO no tsc e obriga a
// decidir a redação junto — o que um arquivo de tradução por SPA não faria.
import type { DenunciaState } from './state-machine.js';

export type EstadoKind = DenunciaState['kind'];

/**
 * Rótulos para o DENUNCIANTE (web-intake, tela de consulta).
 *
 * ⚠️ `AguardandoRetornoComite` colapsa em "Em avaliação pelo comitê", igual a
 * `AvaliacaoEmCurso`, DE PROPÓSITO: a reabertura da instrução é deliberação
 * interna do colegiado. Distinguir os dois na tela pública revelaria que houve
 * divergência no comitê — informação que não pertence ao denunciante e que, em
 * caso pequeno, ajuda a inferir quem votou como. Não "unificar por engano".
 */
export const ESTADO_LABEL_DENUNCIANTE: Readonly<Record<EstadoKind, string>> = {
  Aberta: 'Recebida — aguardando triagem',
  Triagem: 'Em triagem',
  Instrucao: 'Em apuração',
  Oitivas: 'Ouvindo as pessoas envolvidas',
  AvaliacaoEmCurso: 'Em avaliação pelo comitê',
  AguardandoRetornoComite: 'Em avaliação pelo comitê',
  AguardandoDecisao: 'Aguardando decisão',
  Decisao: 'Decidida',
  Recursal: 'Prazo de recurso aberto',
  Encerrada: 'Encerrada',
};

/** Rótulos para quem OPERA o rito (web-comite, web-recurso). */
export const ESTADO_LABEL_COMITE: Readonly<Record<EstadoKind, string>> = {
  Aberta: 'Aberta',
  Triagem: 'Em triagem',
  Instrucao: 'Em apuração',
  Oitivas: 'Em oitivas',
  AvaliacaoEmCurso: 'Em avaliação',
  AguardandoRetornoComite: 'Reaberto — aguardando retorno',
  AguardandoDecisao: 'Aguardando decisão',
  Decisao: 'Decidida',
  Recursal: 'Prazo de recurso',
  Encerrada: 'Encerrada',
};

/**
 * Rótulo do estado na visão do denunciante. Aceita `string` (e não `EstadoKind`)
 * porque o valor chega decifrado do payload e não é validado em runtime: um
 * estado desconhecido tem que virar texto neutro, NUNCA o identificador cru.
 */
export function estadoLabelDenunciante(kind: string): string {
  return ESTADO_LABEL_DENUNCIANTE[kind as EstadoKind] ?? 'Em andamento';
}

/** Rótulo do estado na visão de quem opera o rito. Mesmo contrato de fallback. */
export function estadoLabelComite(kind: string): string {
  return ESTADO_LABEL_COMITE[kind as EstadoKind] ?? 'Estado não identificado';
}
